import { useEffect, useState } from 'react';
import { useUser } from '@/queries/useUser';
import { useIntegrations } from '@/queries/useIntegrations';
import { useCurrencies } from '@/queries/useCurrencies';
import { useCurrencyMutation } from '../../queries/useCurrencyMutation';
import { usePaymentIntegrations } from '../../hooks/usePaymentIntegrations';
import { usePaymentStatus } from '../../hooks/usePaymentStatus';
import { useStripeConnectStatus } from '../../queries/useStripeConnectStatus';
import { useInstantPayoutMutation } from '../../queries/usePaymentMutations';
import { stanNotify } from '@/lib/stanNotify';
import { handleApiError } from '@/lib/utils/apiErrorHandler';
import { EXTERNAL_LINKS } from '@/lib/constants/externalLinks';
import { useExperiments } from '@/features/Experiments/hooks';
import { EXPERIMENTS } from '@/features/Experiments/constants';
import { AdyenPaymentCard, CurrencySection, PaymentModals, PayPalPaymentCard, StripePaymentCard } from './payment';

interface PaymentMethodsSectionProps {
    isAffiliateOnly?: boolean;
}

export const PaymentMethodsSection = ({ isAffiliateOnly = false }: PaymentMethodsSectionProps) => {
    const { user } = useUser();
    const { data: userIntegrations, refetchIntegrations } = useIntegrations();
    const { currencies, isLoading: currenciesLoading } = useCurrencies();

    // Currency state
    const [currency, setCurrency] = useState('USD');
    const [savedCurrency, setSavedCurrency] = useState('USD');
    const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
    const { mutateAsync: updateCurrency, isPending: settingsLoading } = useCurrencyMutation();

    // Modal state
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);

    // Instant payout state
    const [instantPayoutEnabled, setInstantPayoutEnabled] = useState(false);
    const { mutateAsync: toggleInstantPayout, isPending: instantPayoutLoading } = useInstantPayoutMutation();

    const {
        loading: integrationsLoading,
        showProductWarningModal,
        connectStripe,
        connectPayPal,
        connectAdyen,
        manageStripe,
        manageAdyen,
        handleDisconnectStripe,
        handleDisconnectPayPal,
        handlePaymentProcessorConnect,
        handleProductWarningContinue,
        handleProductWarningCancel,
    } = usePaymentIntegrations({ refetchIntegrations });

    // Experiment gating for Adyen
    const { hasExperiment } = useExperiments();
    const isAdyenAllowed = hasExperiment(EXPERIMENTS.ADYEN_ACCOUNT);

    const hasProducts = (user?.active_product_types?.length ?? 0) > 0;
    const isStripeConnectedFromIntegrations = userIntegrations?.['stripe-connect']?.status === 1;
    const { data: stripeConnectStatus } = useStripeConnectStatus(isStripeConnectedFromIntegrations);

    const { isStripeConnected, isPaypalConnected, isAdyenConnected, stripe, paypal, showPaymentWarning } =
        usePaymentStatus(userIntegrations, hasProducts, stripeConnectStatus);

    const isInstantPayoutEligible = stripeConnectStatus?.instant_payout_eligible ?? false;
    const loading = integrationsLoading || settingsLoading;

    // Initialize currency from user data
    useEffect(() => {
        if (user?.data.currency) {
            const curr = user.data.currency.toUpperCase();
            setCurrency(curr);
            setSavedCurrency(curr);
        }
    }, [user?.data.currency]);

    // Initialize instant payout state from user data
    useEffect(() => {
        if (user?.data) {
            setInstantPayoutEnabled(!!user.data.instant_payout);
        }
    }, [user?.data]);

    const handleSaveCurrency = async () => {
        setSaveState('saving');
        await updateCurrency(currency);
        setSavedCurrency(currency);
        setSaveState('saved');
        setTimeout(() => setSaveState('idle'), 2000);
    };

    const handleAppealRejectedAccount = () => {
        window.open(EXTERNAL_LINKS.STRIPE_APPEAL_FORM(user?.user_id || ''), '_blank');
    };

    const handlePayPalHelpDoc = () => {
        window.open(EXTERNAL_LINKS.PAYPAL_CONNECTION_HELP, '_blank');
    };

    const handleMyStoreRedirect = () => {
        window.location.href = EXTERNAL_LINKS.PAGE_CREATE_WITH_LOCATION('store');
    };

    const handleConfirmDisconnectStripe = () => {
        setShowDisconnectModal(false);
        handleDisconnectStripe();
    };

    const handleInstantPayoutToggle = async (enabled: boolean) => {
        try {
            setInstantPayoutEnabled(enabled);
            await toggleInstantPayout(enabled);
            stanNotify({
                type: 'success',
                title: 'Success!',
                text: 'Your payout preference has been updated.',
            });
        } catch (error) {
            setInstantPayoutEnabled(!enabled);
            handleApiError(error, 'Something went wrong');
        }
    };

    return (
        <>
            <div className='w-full max-w-5xl rounded-lg'>
                <div className='mb-2'>
                    <h4>Payment Methods</h4>
                    {showPaymentWarning && (
                        <p className='text-caption-1-regular text-gray mt-2'>
                            Please connect your bank account using a Payment Provider to start selling!
                        </p>
                    )}
                </div>

                <div className='grid w-full grid-cols-1 gap-4 md:grid-cols-2'>
                    {/* Adyen replaces Stripe when experiment is enabled */}
                    {isAdyenAllowed ? (
                        <AdyenPaymentCard
                            isConnected={isAdyenConnected}
                            loading={loading}
                            showAddPayPal={!isPaypalConnected && !isAffiliateOnly}
                            onManage={manageAdyen}
                            onConnect={() => handlePaymentProcessorConnect(connectAdyen, hasProducts)}
                            onAddPayPal={() => handlePaymentProcessorConnect(connectPayPal, hasProducts)}
                        />
                    ) : (
                        <StripePaymentCard
                            isConnected={isStripeConnected}
                            stripe={stripe}
                            isInstantPayoutEligible={isInstantPayoutEligible}
                            instantPayoutEnabled={instantPayoutEnabled}
                            instantPayoutLoading={instantPayoutLoading}
                            showAddPayPal={!isPaypalConnected && !isAffiliateOnly}
                            onManage={manageStripe}
                            onConnect={() => handlePaymentProcessorConnect(connectStripe, hasProducts)}
                            onAppeal={handleAppealRejectedAccount}
                            onInstantPayoutToggle={handleInstantPayoutToggle}
                            onAddPayPal={() => handlePaymentProcessorConnect(connectPayPal, hasProducts)}
                        />
                    )}

                    {isPaypalConnected && (
                        <PayPalPaymentCard
                            paypal={paypal}
                            loading={loading}
                            onDisconnect={handleDisconnectPayPal}
                            onHelpDoc={handlePayPalHelpDoc}
                        />
                    )}
                </div>

                {stripe.chargesEnabled && !isAffiliateOnly && (
                    <CurrencySection
                        currency={currency}
                        savedCurrency={savedCurrency}
                        currencies={currencies}
                        currenciesLoading={currenciesLoading}
                        saveLoading={loading}
                        saveState={saveState}
                        onCurrencyChange={setCurrency}
                        onSave={handleSaveCurrency}
                    />
                )}
            </div>

            <PaymentModals
                showProductWarningModal={showProductWarningModal}
                showDisconnectModal={showDisconnectModal}
                disconnectLoading={loading}
                onProductWarningPrimary={handleMyStoreRedirect}
                onProductWarningSecondary={handleProductWarningContinue}
                onProductWarningClose={handleProductWarningCancel}
                onDisconnectConfirm={handleConfirmDisconnectStripe}
                onDisconnectClose={() => setShowDisconnectModal(false)}
            />
        </>
    );
};
